/**
 * Contenido editorial específico por ciudad para /servicios/seo-local/$ciudad.
 * Las zonas y sectores son reales de cada ciudad: nada de texto genérico
 * reutilizado. Los bloques comunes (metodología, entregables, criterios)
 * se generan a partir de estos datos.
 */

export interface CiudadContenido {
  /** Zonas, barrios o distritos reales donde se concentra la demanda local */
  zonas: string[];
  /** Sectores propuestos para analizar en el diagnóstico */
  sectores: string[];
  /** Contexto de mercado: por qué competir en local aquí es distinto */
  mercado: string[];
  /** Ejemplos de intención de búsqueda a contrastar; no volúmenes medidos */
  busquedas: string[];
  /** Pregunta + respuesta específicas de la ciudad */
  faqs: { question: string; answer: string }[];
}

const c = (
  zonas: string[],
  sectores: string[],
  mercado: string[],
  busquedas: string[],
  faqs: { question: string; answer: string }[],
): CiudadContenido => ({ zonas, sectores, mercado, busquedas, faqs });

export const ciudadesContenido: Record<string, CiudadContenido> = {
  madrid: c(
    ["Salamanca", "Chamberí", "Chamartín", "Centro", "Retiro", "Tetuán", "Moncloa-Aravaca", "Las Rozas", "Pozuelo", "Alcobendas"],
    ["Clínicas y salud privada", "Hostelería y restauración", "Servicios legales y financieros", "Reformas e interiorismo", "Inmobiliaria"],
    [
      "En Madrid analizamos por distrito las consultas de cada servicio y las fichas que compiten en su entorno. Revisamos relevancia, distancia y prominencia para definir las prioridades de ficha y web.",
      "Salamanca, Vallecas y el resto de zonas orientan el análisis de públicos y servicios. Concretamos el área de atención y preparamos páginas de barrio y señales locales según las necesidades del negocio.",
    ],
    ["cerca de mí", "abierto ahora", "urgente 24 horas", "mejor valorado", "precio y presupuesto"],
    [
      {
        question: "¿Se puede competir en Madrid siendo un negocio pequeño?",
        answer:
          "El punto de partida es definir el área de atención y las consultas de cada servicio. Trabajamos la ficha, información específica de barrio y reseñas auténticas para que el negocio compita con una propuesta local clara.",
      },
      {
        question: "¿Trabajáis con varios negocios del mismo sector en Madrid?",
        answer:
          "La exclusividad se define por sector y zona y queda recogida en el contrato. En Madrid analizamos los distritos y áreas de atención para delimitar qué negocios compiten por las mismas consultas.",
      },
    ],
  ),
  barcelona: c(
    ["Eixample", "Gràcia", "Sant Martí / 22@", "Sarrià-Sant Gervasi", "Ciutat Vella", "Les Corts", "Sants-Montjuïc", "Poblenou"],
    ["Turismo y hostelería", "Clínicas y estética", "Tecnología y startups", "Ecommerce", "Servicios profesionales"],
    [
      "En Barcelona analizamos las consultas en castellano y catalán, y valoramos el inglés cuando el negocio atiende visitantes o clientes internacionales. El público real determina los idiomas de la ficha y del contenido.",
      "Eixample, Gràcia y Sant Martí ayudan a concretar servicios y zonas de atención. Adaptamos las páginas locales y la información de la ficha a la cobertura y los idiomas definidos en el diagnóstico.",
    ],
    ["a prop de mi", "cerca de mí", "near me", "obert ara", "opiniones y reseñas"],
    [
      {
        question: "¿Optimizáis también en catalán?",
        answer:
          "Sí. Trabajamos castellano y catalán, y añadimos inglés cuando lo requiere el público del negocio. Revisamos la ficha, las páginas de servicio y las respuestas a reseñas para mantener información útil en cada idioma.",
      },
      {
        question: "¿Cómo se compite con la saturación turística de Ciutat Vella?",
        answer:
          "Analizamos las necesidades de visitantes y residentes que atiende el negocio. A partir de sus idiomas, servicios y zonas, organizamos contenido específico para cada intención relevante.",
      },
    ],
  ),
  valencia: c(
    ["Ruzafa", "Eixample", "Ciutat Vella", "Benimaclet", "Campanar", "El Cabanyal", "Patraix", "Poblats Marítims"],
    ["Hostelería y restauración", "Salud y fisioterapia", "Reformas", "Inmobiliaria", "Servicios a expatriados"],
    [
      "En Valencia combinamos el análisis de servicios para residentes con las necesidades de visitantes que atiende el negocio. Ruzafa, El Cabanyal y el resto de barrios orientan la selección de zonas, consultas e idiomas.",
      "Revisamos fotografías, servicios, horarios y opiniones de la ficha junto con las páginas locales. El diagnóstico ordena las mejoras de ficha y web según la situación de partida y la competencia de cada servicio.",
    ],
    ["cerca de mí", "mejor valorado", "english speaking", "abierto los domingos", "precios"],
    [
      {
        question: "¿Merece la pena optimizar en inglés en Valencia?",
        answer:
          "Analizamos el público y las consultas del negocio. En proyectos de salud, inmobiliaria u hostelería, valoramos qué servicios necesitan información en inglés y cómo incorporarla a la ficha y la web.",
      },
      {
        question: "¿Cuánto se tarda en ver resultados en Valencia?",
        answer:
          "El diagnóstico revisa la ficha, la web y la competencia para definir prioridades. Seguimos su evolución mediante las acciones de la ficha, la visibilidad y los contactos, y ajustamos el trabajo con esos datos.",
      },
    ],
  ),
  sevilla: c(
    ["Nervión", "Triana", "Los Remedios", "Casco Antiguo", "Este-Alcosa", "Macarena", "Bellavista", "Aljarafe"],
    ["Hostelería y eventos", "Bodas y celebraciones", "Salud y estética", "Turismo", "Reformas y climatización"],
    [
      "En Sevilla incorporamos Semana Santa, Feria y eventos al análisis cuando forman parte del calendario comercial del negocio. Revisamos las consultas de cada sector para preparar contenido e información de temporada.",
      "Centro y Aljarafe orientan la delimitación del área de servicio. Coordinamos las páginas locales, publicaciones y reseñas con el calendario del negocio y su capacidad para atender solicitudes.",
    ],
    ["cerca de mí", "para bodas", "abierto en feria", "presupuesto", "mejor valorado"],
    [
      {
        question: "¿Cómo se aprovecha la estacionalidad de Sevilla en SEO local?",
        answer:
          "Revisamos el calendario comercial y las consultas relacionadas con cada temporada. Con esa información programamos páginas y publicaciones antes de los periodos relevantes para el negocio.",
      },
      {
        question: "¿Trabajáis negocios del Aljarafe además del centro?",
        answer:
          "Sí, cuando forman parte del área de atención. Analizamos los servicios que prestas en Tomares, Bormujos, Nervión y otras zonas para definir cobertura, contenido y exclusividad.",
      },
    ],
  ),
  bilbao: c(
    ["Abando", "Indautxu", "Deusto", "Casco Viejo", "Basurto", "Santutxu", "Getxo", "Barakaldo"],
    ["Industria y servicios B2B", "Gastronomía", "Salud privada", "Servicios profesionales", "Turismo urbano"],
    [
      "En Bilbao analizamos las consultas de servicios B2B, gastronomía y atención local según la actividad del negocio. Revisamos fichas, páginas y reseñas por zona para decidir dónde concentrar el trabajo.",
      "Getxo, Barakaldo y otros núcleos del área metropolitana ayudan a concretar la cobertura. Valoramos el uso de euskera y castellano según los clientes y las consultas de cada servicio.",
    ],
    ["cerca de mí", "urgencias", "mejor valorado", "presupuesto", "en Bizkaia"],
    [
      {
        question: "¿El SEO local en Bilbao sirve para empresas B2B?",
        answer:
          "Puede ser relevante para proveedores y servicios B2B con atención en Bizkaia. Revisamos consultas, elegibilidad de la ficha y páginas de servicio para preparar información que facilite el contacto comercial.",
      },
      {
        question: "¿Cubrís el área metropolitana además de Bilbao capital?",
        answer:
          "Sí, según la cobertura del negocio. Getxo, Barakaldo, Leioa y otras zonas del Gran Bilbao se incorporan al análisis de servicios y consultas para decidir qué información propia necesita cada área.",
      },
    ],
  ),
  toledo: c(
    ["Casco Histórico", "Santa Bárbara", "Buenavista", "Santa María de Benquerencia", "Azucaica", "Los Cigarrales"],
    ["Turismo y restauración", "Artesanía y comercio", "Servicios profesionales", "Salud", "Reformas"],
    [
      "En Toledo combinamos el análisis de consultas del Casco Histórico con las necesidades de servicios de Santa Bárbara y Santa María de Benquerencia. El diagnóstico define qué públicos, idiomas y zonas atiende tu negocio.",
      "Revisamos categorías, servicios, fotografías y páginas locales para priorizar las mejoras de ficha y web según tu situación.",
    ],
    ["cerca de mí", "en el casco histórico", "abierto hoy", "opiniones", "precio"],
    [
      {
        question: "¿Compensa invertir en SEO local en una ciudad pequeña como Toledo?",
        answer:
          "Valoramos los objetivos, las consultas disponibles y el valor de cada contacto para tu negocio. El diagnóstico concreta las oportunidades, el trabajo necesario y el alcance de la propuesta.",
      },
      {
        question: "¿Cómo separáis el público turista del residente?",
        answer:
          "Analizamos los idiomas, servicios y necesidades de los visitantes y residentes que atiende el negocio. Organizamos contenido específico cuando cada público necesita información diferente.",
      },
    ],
  ),
  guadalajara: c(
    ["Centro", "Aguas Vivas", "Los Manantiales", "El Balconcillo", "Iriépal", "Cabanillas del Campo", "Azuqueca de Henares"],
    ["Logística y B2B", "Reformas y construcción", "Salud", "Automoción", "Comercio local"],
    [
      "En Guadalajara y el Corredor del Henares combinamos el análisis de servicios industriales con las necesidades de atención residencial. Las consultas y los clientes del negocio orientan el contenido y la cobertura.",
      "Centro, Cabanillas del Campo y Azuqueca de Henares ayudan a delimitar las zonas de trabajo. Revisamos la competencia de cada servicio para priorizar la ficha y las páginas locales.",
    ],
    ["cerca de mí", "en el corredor del henares", "urgente", "presupuesto", "abierto ahora"],
    [
      {
        question: "¿Cubrís Azuqueca, Cabanillas y el resto del Corredor?",
        answer:
          "Sí, cuando el negocio presta servicio en esas zonas. Definimos la cobertura real en Azuqueca, Cabanillas y otros municipios del Corredor y organizamos la información según los servicios de cada área.",
      },
      {
        question: "¿Sirve el SEO local para empresas industriales de la zona?",
        answer:
          "Puede ser pertinente para servicios industriales con cobertura definida. Analizamos consultas B2B, fichas elegibles y páginas de servicio para ordenar las acciones de visibilidad y contacto.",
      },
    ],
  ),
  avila: c(
    ["Centro amurallado", "Zona Norte", "El Seminario", "Las Hervencias", "San Antonio", "Ávila Sur"],
    ["Turismo y hostelería", "Gastronomía", "Comercio local", "Servicios profesionales", "Salud"],
    [
      "En Ávila analizamos consultas de planificación turística junto con los servicios para residentes. El público del negocio y sus necesidades orientan el contenido para el Centro amurallado y las demás zonas de atención.",
      "Coordinamos ficha, fotografías, reseñas y web para ofrecer información útil en cada etapa de la consulta. El diagnóstico identifica las mejoras prioritarias y dónde concentrar el trabajo.",
    ],
    ["qué ver y dónde comer", "cerca de mí", "reservar", "mejor valorado", "abierto domingo"],
    [
      {
        question: "¿Qué peso tienen las reseñas en un mercado turístico como Ávila?",
        answer:
          "Las opiniones auténticas y las respuestas profesionales ayudan a los clientes a conocer el servicio. Revisamos su gestión junto con las fotografías, la ficha y la web para cuidar la información disponible.",
      },
      {
        question: "¿Cómo se capta al visitante madrileño antes de que llegue?",
        answer:
          "Si el negocio atiende visitantes, analizamos sus consultas de planificación. Preparamos información sobre servicios, horarios y disponibilidad real que les ayude a organizar la visita.",
      },
    ],
  ),
  segovia: c(
    ["Centro histórico", "Nueva Segovia", "El Carmen", "San Lorenzo", "La Albuera", "San Cristóbal"],
    ["Restauración", "Turismo y alojamiento", "Comercio", "Servicios profesionales", "Eventos"],
    [
      "En Segovia combinamos consultas de visitantes y residentes por servicio, zona y horario. Centro histórico, Nueva Segovia y los demás barrios permiten concretar la información que necesita cada público.",
      "Actualizamos horarios, fotografías, atributos y servicios de la ficha y revisamos su coherencia con la web. Seguimos las acciones de la ficha y los contactos para orientar los ajustes.",
    ],
    ["cerca de mí", "abierto ahora", "dónde comer", "reservar mesa", "mejor valorado"],
    [
      {
        question: "¿Cómo se gana la búsqueda 'abierto ahora' en Segovia?",
        answer:
          "Mantenemos horarios reales, festivos y servicios actualizados. Revisamos que ficha y web ofrezcan información coherente para quienes buscan un negocio disponible en ese momento.",
      },
      {
        question: "¿Sirve el SEO local si mi cliente viene de fuera de Segovia?",
        answer:
          "Puede ayudar a informar a visitantes antes y durante el viaje. Analizamos consultas de planificación y proximidad para coordinar las páginas de servicio con la ficha del negocio.",
      },
    ],
  ),
  cuenca: c(
    ["Casco Antiguo", "Zona Centro", "Los Tiradores", "El Cañaveras", "Casablanca", "Fuente del Oro"],
    ["Turismo rural y naturaleza", "Restauración", "Comercio local", "Servicios profesionales", "Construcción"],
    [
      "En Cuenca incorporamos el contexto patrimonial y de naturaleza al análisis de turismo y servicios locales. Revisamos consultas, competencia y fichas en el Casco Antiguo y las zonas donde atiende el negocio.",
      "Valoramos las necesidades de quienes planifican desde otras ciudades y de quienes ya están en la zona. Adaptamos páginas, fotografías y servicios de la ficha a la información que busca cada público.",
    ],
    ["turismo rural", "cerca de mí", "casa rural", "dónde comer", "opiniones"],
    [
      {
        question: "¿Hay suficiente volumen de búsqueda en Cuenca para justificar la inversión?",
        answer:
          "Lo valoramos con las consultas disponibles, los objetivos y el valor de cada contacto. El diagnóstico define qué servicios y zonas tienen sentido para el proyecto y qué trabajo necesitan.",
      },
      {
        question: "¿Cómo se capta al turista que planifica desde otra ciudad?",
        answer:
          "Preparamos información útil para planificar: servicios exactos, fotografías actuales y opiniones auténticas. Revisamos las consultas y los contactos para ajustar el contenido a las necesidades del visitante.",
      },
    ],
  ),
  granada: c(
    ["Centro", "Realejo", "Albaicín", "Zaidín", "Ronda", "Chana", "Beiro", "Armilla"],
    ["Turismo y hostelería", "Servicios universitarios", "Salud y estética", "Alojamiento", "Comercio"],
    [
      "En Granada analizamos necesidades vinculadas al turismo, el entorno universitario y los residentes de Zaidín, Chana y otros barrios. Seleccionamos los servicios y consultas relevantes para los públicos que atiende el negocio.",
      "El inicio de curso y los periodos turísticos orientan el calendario cuando forman parte de la actividad. Preparamos contenido e información de ficha según los servicios y la disponibilidad del negocio.",
    ],
    ["cerca de mí", "para estudiantes", "abierto ahora", "mejor valorado", "precio"],
    [
      {
        question: "¿Cómo se aprovecha el mercado universitario de Granada?",
        answer:
          "Analizamos las consultas relacionadas con el inicio de curso cuando el negocio atiende estudiantes. Organizamos páginas, publicaciones e información de servicio según sus necesidades y el calendario comercial.",
      },
      {
        question: "¿Puedo competir con la saturación del centro turístico?",
        answer:
          "Revisamos el servicio, el público y la competencia del centro y de los barrios donde trabajas. El diagnóstico ayuda a decidir qué necesidades de visitantes, residentes o estudiantes debe cubrir tu contenido.",
      },
    ],
  ),
  leon: c(
    ["Centro", "Ensanche", "Barrio Húmedo", "Eras de Renueva", "La Palomera", "Puente Castro", "San Andrés del Rabanedo"],
    ["Gastronomía y tapas", "Turismo del Camino", "Salud", "Servicios profesionales", "Comercio local"],
    [
      "En León incorporamos el Camino de Santiago al análisis de alojamiento, restauración y servicios cuando el negocio atiende peregrinos. Revisamos idiomas, planificación y temporadas para preparar información útil.",
      "Ensanche y Eras de Renueva orientan el trabajo de servicios para residentes. Analizamos las consultas y fichas de cada zona para coordinar cobertura, contenido y prioridades.",
    ],
    ["cerca de mí", "camino de santiago", "abierto ahora", "menú del día", "opiniones"],
    [
      {
        question: "¿Cómo se capta al peregrino del Camino en León?",
        answer:
          "Revisamos idiomas, servicios, horarios y consultas de planificación y proximidad. Coordinamos ficha y web para ofrecer al peregrino información útil sobre lo que puede encontrar en el negocio.",
      },
      {
        question: "¿La estacionalidad del Camino afecta a la estrategia?",
        answer:
          "La estudiamos según la actividad y los datos comerciales del negocio. Organizamos contenido y publicaciones alrededor de los periodos relevantes y revisamos también las necesidades del público residente.",
      },
    ],
  ),
  valladolid: c(
    ["Centro", "Delicias", "Parquesol", "La Rondilla", "Huerta del Rey", "Covaresa", "Arturo Eyries", "Laguna de Duero"],
    ["Industria y automoción", "Servicios B2B", "Salud", "Hostelería", "Comercio y reformas"],
    [
      "En Valladolid analizamos servicios industriales, B2B y atención local según la actividad de la empresa. Revisamos consultas, público y cobertura en Centro, Delicias, Parquesol y las demás zonas de trabajo.",
      "Preparamos páginas de servicio con información técnica y fichas completas que faciliten la evaluación del cliente. Los datos del diagnóstico determinan qué contenido y mejoras desarrollar primero.",
    ],
    ["cerca de mí", "empresa de", "presupuesto", "urgente", "mejor valorado"],
    [
      {
        question: "¿Funciona el SEO local para empresas industriales de Valladolid?",
        answer:
          "Puede ser útil para servicios industriales con intención local. Revisamos consultas, fichas y contenido de competidores para definir páginas técnicas y una información de empresa clara.",
      },
      {
        question: "¿Cuánto contenido hace falta para competir aquí?",
        answer:
          "Lo definimos según los servicios y las preguntas reales del cliente. Priorizamos información técnica precisa y contenido propio de las zonas donde el negocio presta servicio.",
      },
    ],
  ),
  asturias: c(
    ["Oviedo", "Gijón", "Avilés", "Siero-Pola de Siero", "Langreo", "Mieres", "Llanes", "Cangas de Onís"],
    ["Hostelería y sidrerías", "Turismo rural y naturaleza", "Salud", "Construcción y reformas", "Servicios profesionales"],
    [
      "Asturias se trabaja como un ámbito regional: Oviedo, Gijón, Avilés y las comarcas se analizan según la cobertura del negocio. Revisamos consultas y estacionalidad por actividad y núcleo de atención.",
      "Definimos las zonas reales de servicio y la información que aporta cada una. Organizamos la arquitectura de la web y las fichas elegibles según las necesidades del proyecto.",
    ],
    ["cerca de mí", "en Oviedo", "en Gijón", "turismo rural", "abierto hoy"],
    [
      {
        question: "¿Se puede posicionar en toda Asturias con una sola web?",
        answer:
          "Una web puede organizar la información de los distintos núcleos donde presta servicio el negocio. Definimos páginas de zona cuando los servicios o la información propia de cada área lo justifican.",
      },
      {
        question: "¿Cómo se gestiona la estacionalidad turística del Principado?",
        answer:
          "Revisamos el calendario del negocio y las consultas de cada periodo. Coordinamos contenido, ficha y opiniones para ofrecer información útil al visitante y al público residente.",
      },
    ],
  ),
  malaga: c(
    ["Centro Histórico", "Teatinos", "El Palo", "Pedregalejo", "Huelin", "Churriana", "Marbella", "Torremolinos", "Fuengirola"],
    ["Tecnología y servicios digitales", "Turismo y hostelería", "Salud y estética", "Inmobiliaria", "Servicios a expatriados"],
    [
      "En Málaga combinamos el análisis de turismo, servicios tecnológicos y atención residencial según la actividad del negocio. Revisamos consultas, idiomas y competencia para concretar el trabajo de ficha y web.",
      "Marbella, Torremolinos y Fuengirola se incorporan cuando forman parte de la cobertura real. Analizamos los servicios de cada zona para definir información propia e idiomas adecuados al público.",
    ],
    ["near me", "cerca de mí", "english speaking", "en la costa del sol", "mejor valorado"],
    [
      {
        question: "¿Hay que optimizar en inglés en Málaga?",
        answer:
          "Lo decidimos según el público y las consultas del negocio. Revisamos qué servicios necesitan información en inglés y cómo incorporarla de forma coherente a la ficha y las páginas locales.",
      },
      {
        question: "¿Cubrís Marbella, Torremolinos y Fuengirola?",
        answer:
          "Sí, cuando el negocio presta servicio en esas áreas. Concretamos la cobertura y preparamos información diferenciada para los servicios que ofrece en cada zona.",
      },
    ],
  ),
  zamora: c(
    ["Centro histórico", "San José Obrero", "Los Bloques", "Pinilla", "La Horta", "Cabañales"],
    ["Restauración", "Turismo del románico", "Comercio local", "Servicios profesionales", "Agroalimentario"],
    [
      "En Zamora revisamos las consultas y fichas de cada servicio para valorar el recorrido del proyecto. Centro histórico, La Horta y los demás barrios ayudan a delimitar la atención local del negocio.",
      "El románico, Semana Santa y el contexto agroalimentario orientan el análisis de necesidades turísticas y B2B. Organizamos contenido por servicio y público según los datos del diagnóstico.",
    ],
    ["cerca de mí", "dónde comer", "abierto hoy", "opiniones", "presupuesto"],
    [
      {
        question: "¿Compensa el SEO local en un mercado tan pequeño?",
        answer:
          "Lo valoramos según los objetivos, las consultas y el valor de cada contacto. Revisamos las oportunidades y el esfuerzo necesario para definir una propuesta adecuada al negocio.",
      },
      {
        question: "¿Puede un negocio de Zamora captar clientes de fuera de la provincia?",
        answer:
          "Puede ser relevante para negocios que atienden clientes de otras zonas, como turismo, agroalimentario o servicios especializados. Combinamos información de producto o servicio con contenido de cobertura según el público real.",
      },
    ],
  ),
  lugo: c(
    ["Centro / Muralla", "A Milagrosa", "Fingoi", "O Carme", "Recatelo", "Piringalla"],
    ["Hostelería y gastronomía", "Turismo del Camino Primitivo", "Agroalimentario", "Servicios profesionales", "Salud"],
    [
      "En Lugo, el patrimonio, Camino Primitivo y contexto agroalimentario orientan el análisis de servicios y públicos. Revisamos consultas, fichas y competencia en las zonas donde trabaja el negocio.",
      "Valoramos castellano y gallego según las consultas y los clientes. Adaptamos ficha y contenido para describir con precisión los servicios, lugares e información que necesita cada público.",
    ],
    ["preto de min", "cerca de mí", "camino primitivo", "dónde comer", "abierto ahora"],
    [
      {
        question: "¿Trabajáis el contenido en gallego?",
        answer:
          "Sí, cuando lo requiere el público del negocio. El diagnóstico determina qué servicios y páginas necesitan información en gallego y cómo mantenerla coherente con la ficha.",
      },
      {
        question: "¿Cómo se capta al peregrino del Camino Primitivo?",
        answer:
          "Revisamos servicios, horarios, idiomas y necesidades de planificación cuando el negocio atiende peregrinos. Coordinamos ficha y web para ofrecer información práctica sobre la atención disponible.",
      },
    ],
  ),
  murcia: c(
    ["Centro", "La Flota", "Santiago el Mayor", "Espinardo", "El Ranero", "Vistalegre", "Molina de Segura", "Alcantarilla"],
    ["Agroalimentario y exportación", "Salud y estética", "Hostelería", "Reformas y climatización", "Servicios profesionales"],
    [
      "En Murcia analizamos servicios urbanos, agroalimentarios y B2B según la empresa. Molina de Segura, Alcantarilla y Espinardo ayudan a concretar cobertura y consultas de las áreas donde trabaja.",
      "En climatización, piscinas o reformas revisamos el calendario comercial y las consultas de temporada. Coordinamos contenido y ficha con los servicios y la disponibilidad del negocio.",
    ],
    ["cerca de mí", "urgente hoy", "presupuesto", "en la Región de Murcia", "mejor valorado"],
    [
      {
        question: "¿Cubrís el área metropolitana de Murcia?",
        answer:
          "Sí, según el área real de servicio. Analizamos Molina de Segura, Alcantarilla, Espinardo y las demás zonas de atención para decidir qué información específica necesita cada una.",
      },
      {
        question: "¿Cómo se trabaja un sector estacional como climatización?",
        answer:
          "Revisamos el calendario comercial y las consultas antes de preparar contenido y actualizar la ficha. Programamos la información de servicios y disponibilidad según los periodos relevantes para el negocio.",
      },
    ],
  ),
  zaragoza: c(
    ["Centro", "Delicias", "Actur", "Universidad", "Casablanca", "Las Fuentes", "Miralbueno", "San José"],
    ["Logística y B2B", "Salud", "Hostelería", "Comercio y reformas", "Servicios profesionales"],
    [
      "En Zaragoza, la ubicación entre Madrid, Barcelona y Bilbao y el contexto logístico orientan el análisis B2B y local. Revisamos las consultas y la competencia de los servicios que ofrece la empresa.",
      "Delicias, Actur y Centro ayudan a concretar el área de atención. Desarrollamos contenido de zona cuando aporta información útil y diferenciada sobre los servicios del negocio.",
    ],
    ["cerca de mí", "empresa de", "abierto ahora", "presupuesto", "urgente"],
    [
      {
        question: "¿Es Zaragoza un mercado competitivo en SEO local?",
        answer:
          "La competencia se analiza por servicio y área de atención. Revisamos consultas y fichas reales para identificar diferencias y ordenar el trabajo que necesita el proyecto.",
      },
      {
        question: "¿Trabajáis por distritos dentro de Zaragoza?",
        answer:
          "Sí, cuando el proyecto necesita información diferenciada por zona. Definimos áreas de atención y contenido propio para Actur, Delicias y los demás distritos donde trabaja el negocio.",
      },
    ],
  ),
  alicante: c(
    ["Centro", "Playa de San Juan", "Albufereta", "Benalúa", "Carolinas", "San Blas", "El Campello", "Sant Joan d'Alacant"],
    ["Turismo y hostelería", "Salud y clínicas", "Inmobiliaria", "Servicios a expatriados", "Reformas"],
    [
      "En Alicante analizamos las necesidades de residentes, visitantes y público internacional que atiende el negocio. Las consultas y los clientes orientan los idiomas y el contenido de cada servicio.",
      "Playa de San Juan, El Campello y Sant Joan ayudan a definir cobertura y calendario comercial. Revisamos qué información necesita cada zona antes de preparar páginas y actualizar la ficha.",
    ],
    ["near me", "cerca de mí", "english speaking", "abierto ahora", "mejor valorado"],
    [
      {
        question: "¿Merece la pena optimizar en inglés en Alicante?",
        answer:
          "Lo valoramos con las consultas y el público del negocio. Definimos qué servicios necesitan información en inglés y coordinamos su contenido con la ficha y las páginas locales.",
      },
      {
        question: "¿Cómo afecta la estacionalidad turística?",
        answer:
          "Revisamos los datos comerciales y las consultas de cada temporada. Preparamos información antes de los periodos relevantes y atendemos también las necesidades del público residente.",
      },
    ],
  ),
};

export function getCiudadContenido(slug: string): CiudadContenido | null {
  return ciudadesContenido[slug] ?? null;
}
